import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LessonCom from "./LessonCom";
import { GLOBAL_IMAGES, YearBg } from "../../assets/asset";
import { LessonSkeleton, SkeletonHeader } from "../../utils/SkeletonLoader";
import { UseYearColor } from "../../hooks/useYearColor";
import { useDispatch, useSelector } from "react-redux";
import { setScrollPosition } from "../../store/slice/scrollPositionSlice";
import axiosInstance from "../../api/axiosInstance";
import Knowledge from "./Knowledge";
import { setShowKnowledge } from "../../store/slice/profileSlice";
import { setOlderValueWas } from "../../store/slice/muteSlice";
import backArrowImage from "../../assets/global/Back.png";
import YearCompletionPopup from "../ui/YearCompletionPopup";
import { useUpdateUser } from "../../features/auth/useLogin";
import { useYears } from "../../features/queryHooks/useYears";
import { useSubscription } from "../../context/SubscriptionContext";
import {
  getAccessibleBasicYearId,
  getCanonicalYearId,
  getCurrentUnlockedYearId,
  getNextYearMeta,
  getUserId,
  isPaidBasicUser,
} from "../../utils/basicPlanAccess";
import { toast } from "react-toastify";

// Loading Screen Component
const LoadingScreen = () => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900">
    <div className="text-center">
      <div className="relative mb-8">
        <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-white"></div>
        <div className="absolute inset-0 rounded-full border-2 border-white opacity-30"></div>
      </div>
      <h2 className="text-3xl font-bold text-white mb-3 font-mali">Loading Lessons</h2>
      <p className="text-blue-200 text-lg font-mali">Preparing your learning experience...</p>
      <div className="mt-6 flex justify-center space-x-2">
        <div className="w-3 h-3 bg-white rounded-full animate-bounce"></div>
        <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
        <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
      </div>
    </div>
  </div>
);

const LessonDashboard = () => {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { showKnowledge, currentYear } = useSelector((state) => state.profile);
  const { normalizedPlanType } = useSubscription();
  const [selectedIcon, setSelectedIcon] = useState("notebook");
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const { scrollPosition, activeYear } = useSelector(
    (state) => state.scrollPosition
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [lastLessonPKValue, setLastLessionPKValue] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { yearId: stateYearId, yearCompletionStatus } = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const persistedYearId = getCanonicalYearId(currentYear);
  const rawYearId =
    stateYearId ??
    searchParams.get("yearId") ??
    persistedYearId ??
    null;
  const yearId = Number(rawYearId);
  const isYearIdValid = Number.isFinite(yearId) && yearId > 0;
  const safeYearCompletionStatus = +yearCompletionStatus;
  const isBasicPlanUser =
    normalizedPlanType === "basic" || isPaidBasicUser(user);
  const currentUnlockedYearId = isBasicPlanUser
    ? getAccessibleBasicYearId(user)
    : getCurrentUnlockedYearId(user);

  const scrollContainerRef = useRef(null);
  const yearAdvanceRef = useRef("");
  const colorTheme = UseYearColor(isYearIdValid ? yearId - 1 : 0);
  const updateUserMutation = useUpdateUser();
  const yearsQuery = useYears();

  const scrollAmount = window.innerWidth * 0.8;

  // Image preloading functions
  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      if (!src) {
        resolve(); // Skip loading if src is null/undefined
        return;
      }
      
      const img = new Image();
      img.onload = resolve;
      img.onerror = () => {
        console.warn(`Failed to load image: ${src}`);
        resolve(); // Resolve instead of reject to not block the page
      };
      img.src = src;
    });
  };

  const preloadAllImages = async () => {
    const imagesToPreload = [
      isYearIdValid ? YearBg[yearId - 1] : null, // Background image for current year
      GLOBAL_IMAGES.dingo, // Logo
      GLOBAL_IMAGES.notebook, // Notebook icon
      GLOBAL_IMAGES.knowledgeCard, // Knowledge card icon
      GLOBAL_IMAGES.medal, // Medal icon
      user?.profilephotos, // User profile photo (optional)
      backArrowImage, // Back arrow
      GLOBAL_IMAGES.next, // Next arrow
    ].filter(Boolean); // Remove null/undefined values

    try {
      await Promise.all(imagesToPreload.map(src => preloadImage(src)));
      setImagesLoaded(true);
    } catch (error) {
      console.error('Error preloading images:', error);
      // Still show the page even if some images fail to load
      setImagesLoaded(true);
    }
  };

  // Preload images on component mount
  useEffect(() => {
    preloadAllImages();
  }, [yearId, user?.profilephotos, isYearIdValid]);

  useEffect(() => {
    const handleScrollCheck = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        setCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth
        );
      }
    };

    setTimeout(handleScrollCheck, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        dispatch(setScrollPosition(container.scrollLeft));
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth
        );
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [dispatch]);

  // Scroll to the last-clicked lesson when returning from a completed lesson.
  // We read the saved lesson index from sessionStorage (set in handleLessonClick)
  // and scroll to that element once everything is rendered.
  useEffect(() => {
    if (!imagesLoaded || loading || !data?.data?.length) return;
    if (!scrollContainerRef.current) return;

    let savedIndex = -1;
    try {
      savedIndex = Number(sessionStorage.getItem("lastClickedLessonIndex") ?? -1);
    } catch (_) {
      savedIndex = -1;
    }

    if (savedIndex < 0) return;

    const timerId = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const targetEl = container.querySelector(
        `[data-lesson-index="${savedIndex}"]`
      );
      if (!targetEl) return;

      const containerRect = container.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();
      const scrollOffset =
        targetRect.left -
        containerRect.left +
        container.scrollLeft -
        containerRect.width / 3;

      container.scrollTo({
        left: Math.max(0, scrollOffset),
        behavior: "smooth",
      });

      // Clear so it doesn't re-scroll on next render
      try { sessionStorage.removeItem("lastClickedLessonIndex"); } catch (_) {}
    }, 400);
    return () => clearTimeout(timerId);
  }, [data, loading, imagesLoaded]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft -= scrollAmount;
      dispatch(setScrollPosition(scrollContainerRef.current.scrollLeft));
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += scrollAmount;
      dispatch(setScrollPosition(scrollContainerRef.current.scrollLeft));
    }
  };

  const processLessons = (lessons) => {
    if (!lessons) return [];

    let regularLessonCount = 0;
    let testLessonCount = 0;

    return lessons.map((lesson, index) => {
      const isTestLesson = lesson.fields.lession.toLowerCase().includes("test");
      let displayNumber;

      if (isTestLesson) {
        testLessonCount++;
        displayNumber = `T${testLessonCount}`;
      } else {
        regularLessonCount++;
        displayNumber = regularLessonCount;
      }

      return {
        ...lesson,
        isTestLesson,
        displayNumber,
        displayIndex: index,
      };
    });
  };

  useEffect(() => {
    if (!isYearIdValid) {
      setLoading(false);
      navigate("/yearsdashboard", { replace: true });
      return;
    }

    const fetchData = async () => {
      try {
        const result = await axiosInstance.post("api/lessionofyear", {
          YearId: yearId,
        });
        const processedData = {
          ...result,
          data: processLessons(result.data.data),
        };
        setData(processedData);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [yearId, isYearIdValid, navigate]);

  useEffect(() => {
    if (data) setLastLessionPKValue(data?.data[data?.data.length - 1]?.pk);
  }, [data]);

  useEffect(() => {
    if (!isYearIdValid || !data?.data?.length) return;
    if (!isBasicPlanUser) return;
    if (currentUnlockedYearId !== yearId) return;

    const allLessonsCompleted = data.data.every(
      (lesson) => String(lesson?.fields?.attempt) === "1"
    );

    if (!allLessonsCompleted) return;

    const nextYearMeta = getNextYearMeta(yearId, yearsQuery.data?.data ?? []);
    if (!nextYearMeta?.yearid) return;

    const advanceKey = `${yearId}:${nextYearMeta.yearid}`;
    if (yearAdvanceRef.current === advanceKey) return;

    yearAdvanceRef.current = advanceKey;
    updateUserMutation.mutate(
      {
        id: getUserId(user),
        yearid: nextYearMeta.yearid,
        year: nextYearMeta.year,
      },
      {
        onError: (error) => {
          toast.error(
            error?.response?.data?.message ||
              "Unable to unlock the next year right now.",
            {
              ariaLabel: "Year Unlock Error",
              autoClose: 2000,
              theme: "colored",
              hideProgressBar: true,
              className: "font-mali",
            }
          );
        },
      }
    );
  }, [
    currentUnlockedYearId,
    data,
    isBasicPlanUser,
    isYearIdValid,
    updateUserMutation,
    user,
    yearId,
    yearsQuery.data?.data,
  ]);

  const handleLessonClick = (lesson) => {
    const lessonId = lesson.pk;
    const lessonContext = {
      lessonId,
      yearId,
      colorTheme,
      lessonDisplayName: lesson.displayNumber,
    };

    try {
      sessionStorage.setItem("lessonContext", JSON.stringify(lessonContext));
      // Save lesson index so we can scroll back to this position on return
      sessionStorage.setItem("lastClickedLessonIndex", String(lesson.displayIndex));
    } catch (_) {
      // Ignore storage errors.
    }

    dispatch(setOlderValueWas());
    navigate(
      `/foundationLessonCom?lessonId=${lessonId}&yearId=${yearId}&lessonDisplayName=${encodeURIComponent(
        lesson.displayNumber
      )}`,
      {
      state: { ...lessonContext, lastLesson: lastLessonPKValue },
      }
    );
  };

  const renderLessons = () => {
    if (!data?.data) return null;

    return data.data.map((lesson) => {
      const lessonClass = `flex-shrink-0 ${
        lesson.displayIndex % 2 === 0 ? "relative -top-4" : "relative top-4"
      }`;
      return (
        <div className={`${lessonClass}`} key={lesson.pk} data-lesson-index={lesson.displayIndex}>
          <LessonCom
            lessonTopics={lesson.fields.lessonTopics}
            index={lesson.displayIndex}
            yearId={yearId}
            isAttempt={lesson.fields.attempt}
            isTestLesson={lesson.isTestLesson}
            displayNumber={lesson.displayNumber}
            onClick={() => handleLessonClick(lesson)}
          />
        </div>
      );
    });
  };

  const closeKnowledge = () => {
    dispatch(setShowKnowledge(false));
    setSelectedIcon("notebook");
  };

  // Show loading screen until images are loaded
  if (!imagesLoaded) {
    return <LoadingScreen />;
  }

  return (
    <div
      className="flex w-screen min-h-screen overflow-y-auto items-center"
      style={{
        backgroundImage: `url(${isYearIdValid ? YearBg[yearId - 1] : GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        minHeight: "100vh",
      }}
    >
      {safeYearCompletionStatus === 1 && <YearCompletionPopup />}
      {showKnowledge && (
        <Knowledge closeKnowledge={closeKnowledge} year={yearId} />
      )}

      <>
        <nav
          className="fixed z-10 top-0 left-0 w-full flex items-center justify-between px-6 py-4 font-mali"
          style={{ background: "rgba(0, 0, 0, 0.60)" }}
        >
          {loading ? (
            <SkeletonHeader />
          ) : (
            <>
              <div className="flex items-center gap-5 ">
                <Link to={`/yearsdashboard`}>
                  <img
                    src={GLOBAL_IMAGES.dingo}
                    alt="Logo"
                    className="h-16 w-16"
                  />
                </Link>
                <h1 className="ml-4 text-white text-3xl font-bold">
                  Year {yearId === 1 ? "Foundation" : yearId - 1}
                </h1>
              </div>
              <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-10">
                <div className="flex flex-col items-center">
                  <img
                    src={GLOBAL_IMAGES.notebook}
                    className="w-16 h-16 cursor-pointer "
                    onClick={() => setSelectedIcon("notebook")}
                  />
                  {selectedIcon === "notebook" && (
                    <div className="h-2 w-2 rounded-full bg-blue-500 mt-1"></div>
                  )}
                </div>

                <div
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => {
                    dispatch(setShowKnowledge(true));
                    setSelectedIcon("knowledge");
                  }}
                >
                  <img
                    src={GLOBAL_IMAGES.knowledgeCard}
                    className="w-16 h-16 "
                    alt="Knowledge Card"
                  />
                  {selectedIcon === "knowledge" && (
                    <div className="h-2 w-2 rounded-full bg-blue-500 mt-1"></div>
                  )}
                </div>

                <div className="flex flex-col items-center">
                  <Link
                    to="/leaderboard"
                    onClick={() => setSelectedIcon("medal")}
                  >
                    <img src={GLOBAL_IMAGES.medal} className="w-16 h-16" />
                  </Link>
                  {selectedIcon === "medal" && (
                    <div className="h-2 w-2 rounded-full bg-blue-500 mt-1"></div>
                  )}
                </div>
              </div>
              <Link to={`/settings`}>
                <img
                  src={user.profilephotos && user.profilephotos}
                  alt="profile-avatar"
                  className="h-16 w-16 rounded-full"
                />
              </Link>
            </>
          )}
        </nav>

        <div className="flex items-center    w-screen self-center mx-auto absolute z-10 top-[80px] mt-4">
          <div className="flex items-center w-full justify-between mx-32">
            {canScrollLeft && (
              <div
                onClick={scrollLeft}
                className="cursor-pointer items-center justify-center  "
              >
                <img src={backArrowImage} className="" alt="Back" />
              </div>
            )}
            {canScrollRight && (
              <div
                onClick={scrollRight}
                className="cursor-pointer items-center justify-center relative"
                style={{ marginLeft: canScrollLeft ? "0" : "auto" }}
              >
                <img src={GLOBAL_IMAGES.next} className="z-[30] " alt="Next" />
              </div>
            )}
          </div>
        </div>

        <div
          ref={scrollContainerRef}
          className="flex  items-center gap-16 overflow-x-auto w-full px-20 mt-12 scroll-smooth"
          style={{
            minWidth: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "20px",
          }}
        >
          <div className="flex gap-16 min-w-max px-10">
            {loading
              ? [...Array(5)].map((_, index) => (
                  <LessonSkeleton key={index} index={index} />
                ))
              : renderLessons()}
          </div>
        </div>
      </>
    </div>
  );
};

export default LessonDashboard;
